import { useToastWrapper } from 'hooks/useToastWrapper';
import { useEffect } from 'react';
import { Navigate, Route, Routes as RouterRoutes, useLocation, useSearchParams } from 'react-router-dom';

import { AchievementProvider } from 'auth/AchievementProvider';
import { useAuth } from 'auth/AuthProvider';
import { CurrentUserProvider, useCurrentUser } from 'auth/CurrentUserProvider';
import AuthGate from 'components/AuthGate';
import { REF_COOKIE_KEY } from 'config/constants/github';
import Billing from 'features/billing';
import { BillingProvider } from 'features/billing/context/BillingProvider';
import StripeCallback from 'features/callbacks/StripeCallback';
import ChallengeHub from 'features/challenge-hub';
import Challenges from 'features/challenges';
import CodeFrameHub from 'features/code-frame-hub';
import NewCodeFrame from 'features/code-frame-hub/NewCodeFrame';
import CodeFrames from 'features/code-frames';
import CVBuilder from 'features/cv-builder';
import CVEditor from 'features/cv-builder/CVEditor';
import CVPublicView from 'features/cv-builder/CVPublicView';
import CVStreaming from 'features/cv-builder/CVStreaming';
import NewCV from 'features/cv-builder/NewCV';
import Dashboard from 'features/dashboard';
import Demo from 'features/demo/Demo';
import EditProfile from 'features/edit-profile';
import Onboarding from 'features/onboarding/steps/Onboarding';
import OnboardingPaywall from 'features/onboarding/steps/OnboardingPaywall';
import OnboardingPersonalizeStep from 'features/onboarding/steps/OnboardingPersonalizeStep';
import OnboardingProfileStep from 'features/onboarding/steps/OnboardingProfileStep';
import PracticeProblemHub from 'features/practice-problem-hub';
import PracticeProblems from 'features/practice-problems/PracticeProblems';
import PreviewProfile from 'features/preview-profile';
import Pricing from 'features/pricing';
import ProjectHub from 'features/project-hub';
import Projects from 'features/projects';
import PrototypeHub from 'features/prototype-hub';
import NewPrototype from 'features/prototype-hub/NewPrototype';
import Prototypes from 'features/prototypes';
import Roasts from 'features/roast';
import RoastDetail from 'features/roast/components/RoastDetail';
import Settings from 'features/settings';
import { ERoute } from 'types/frontend';
import { setCookie } from 'utils/cookies';
import { captureFbclid, generateFbp, initPixelWithConsent } from 'utils/metaTracking';
import { savePlanPreference } from 'utils/planPreference';

import LegacySolutionRedirect from './LegacySolutionRedirect';
import DiscordCallback from './features/callbacks/DiscordCallback';
import { AppLayout, OnboardingLayout } from './layout';

const DEFAULT_DEMO = `${ERoute.Demo}/custom/browser`;

// Redirects non-public paths to demo editor with sign-up prompt
const DemoRedirect = () => <Navigate to={`${DEFAULT_DEMO}?redirect=true`} replace />;

// Path prefixes that render actual public content for anon users (need AppLayout).
// Only solution sub-paths are public — listing pages (/projects, /challenges, etc.)
// redirect to demo. Pricing lives on the landing page, not in the app.
const UNAUTH_CONTENT_PREFIXES = [
  '/demo/',
  '/profile/',
  // Public solution/hub routes (not listing pages)
  '/projects/',
  '/challenges/',
  '/problems/',
  '/prototypes/',
  '/code-frames/',
  // Legacy solution routes
  '/challenge-solutions/',
  '/challenge-code-previews/',
  '/solutions/',
  '/project-code-previews/',
];

const isUnauthContentPath = (pathname: string) =>
  UNAUTH_CONTENT_PREFIXES.some((prefix) => pathname.startsWith(prefix));

/**
 * Routes available without authentication.
 * Hub routes (with screen name) are public — solutions accessible without login.
 * Listing pages (/projects, /challenges, etc.) fall through to catch-all → landing redirect.
 *
 * Redirect-only paths (/, catch-all) skip AppLayout to prevent sidebar flash.
 */
const UnauthenticatedRoutes = () => {
  const { pathname } = useLocation();

  // Redirect-only paths — skip layout entirely to prevent sidebar/header flash.
  // /onboarding renders AuthGate directly (no AppLayout) to avoid sidebar blink.
  if (!isUnauthContentPath(pathname)) {
    return (
      <RouterRoutes>
        <Route path="/" element={<Navigate to={DEFAULT_DEMO} replace />} />
        <Route path={ERoute.Onboarding} element={<AuthGate />} />
        <Route path="*" element={<DemoRedirect />} />
      </RouterRoutes>
    );
  }

  return (
    <AppLayout isFreeUser hasDiscordRegistered={false}>
      <RouterRoutes>
        {/* Legacy routes for solutions */}
        <Route
          path={`/challenge-solutions/:userScreenName/:challengeScreenName/*`}
          element={<LegacySolutionRedirect type="challenge" />}
        />
        <Route
          path={`/challenge-code-previews/:previewUniqueName/*`}
          element={<LegacySolutionRedirect type="challenge" />}
        />
        <Route
          path={`/solutions/:userScreenName/:projectScreenName/*`}
          element={<LegacySolutionRedirect type="project" />}
        />
        <Route
          path={`/project-code-previews/:previewUniqueName/*`}
          element={<LegacySolutionRedirect type="project" />}
        />

        {/* Hub routes — Hub's internal routing handles solution vs playground/submit.
             Anon users hitting the playground catch-all get redirected to demo. */}
        <Route path={`${ERoute.Projects}/:projectScreenName/*`} element={<ProjectHub />} />
        <Route path={`${ERoute.Challenges}/:challengeScreenName/*`} element={<ChallengeHub />} />
        <Route
          path={`${ERoute.PracticeProblems}/:practiceProblemScreenName/*`}
          element={<PracticeProblemHub />}
        />
        <Route
          path={`${ERoute.Prototypes}/:userScreenName/:prototypeScreenName/*`}
          element={<PrototypeHub />}
        />
        <Route
          path={`${ERoute.CodeFrames}/:userScreenName/:codeFrameScreenName/*`}
          element={<CodeFrameHub />}
        />

        {/* Demo routes */}
        <Route path={ERoute.Demo} element={<Navigate to={DEFAULT_DEMO} replace />} />
        <Route path={`${ERoute.Demo}/:template/*`} element={<Demo />} />

        {/* Public profile preview */}
        <Route path={`${ERoute.Profile}/:profileScreenName`} element={<PreviewProfile />} />

        {/* Fallback for content paths that don't match above */}
        <Route path="*" element={<DemoRedirect />} />
      </RouterRoutes>
    </AppLayout>
  );
};

const OnboardingRoutes = () => (
  <OnboardingLayout>
    <RouterRoutes>
      <Route path={ERoute.Onboarding} element={<Onboarding />} />
      <Route path="*" element={<Navigate to={ERoute.Onboarding} replace />} />
    </RouterRoutes>
  </OnboardingLayout>
);

const ProfileRoutes = () => (
  <OnboardingLayout step={1} totalSteps={3}>
    <RouterRoutes>
      <Route path={ERoute.Onboarding} element={<OnboardingProfileStep />} />
      <Route path="*" element={<Navigate to={ERoute.Onboarding} replace />} />
    </RouterRoutes>
  </OnboardingLayout>
);

const PersonalizeRoutes = () => (
  <OnboardingLayout step={2} totalSteps={3}>
    <RouterRoutes>
      <Route path={ERoute.Onboarding} element={<OnboardingPersonalizeStep />} />
      <Route path="*" element={<Navigate to={ERoute.Onboarding} replace />} />
    </RouterRoutes>
  </OnboardingLayout>
);

const PaywallRoutes = () => (
  <OnboardingLayout step={3} totalSteps={3}>
    <RouterRoutes>
      <Route path={ERoute.Onboarding} element={<OnboardingPaywall />} />
      <Route
        path={ERoute.StripeCallback}
        element={
          <BillingProvider>
            <StripeCallback />
          </BillingProvider>
        }
      />
      <Route path="*" element={<Navigate to={ERoute.Onboarding} replace />} />
    </RouterRoutes>
  </OnboardingLayout>
);

const AppRoutes = () => {
  const { githubAccessToken, isFree, hasDiscordRegistered } = useCurrentUser();
  const { signOut, userId: authUserId } = useAuth();
  const { toastInfo } = useToastWrapper();

  useEffect(() => {
    const checkSignOut = async () => {
      if (!githubAccessToken && authUserId) {
        await signOut();

        toastInfo('Session expired, please sign in again.');
      }
    };

    checkSignOut();
  }, [githubAccessToken, authUserId]);

  return (
    <AppLayout isFreeUser={isFree} hasDiscordRegistered={hasDiscordRegistered}>
      <RouterRoutes>
        {/* Legacy routes for solutions */}
        <Route
          path={`/challenge-solutions/:userScreenName/:challengeScreenName/*`}
          element={<LegacySolutionRedirect type="challenge" />}
        />
        <Route
          path={`/challenge-code-previews/:previewUniqueName/*`}
          element={<LegacySolutionRedirect type="challenge" />}
        />
        <Route
          path={`/solutions/:userScreenName/:projectScreenName/*`}
          element={<LegacySolutionRedirect type="project" />}
        />
        <Route
          path={`/project-code-previews/:previewUniqueName/*`}
          element={<LegacySolutionRedirect type="project" />}
        />

        <Route path={ERoute.Dashboard} element={<Dashboard />} />

        {/* Learn routes */}
        <Route path={ERoute.Projects} element={<Projects />} />
        <Route path={ERoute.Challenges} element={<Challenges />} />
        <Route path={ERoute.PracticeProblems} element={<PracticeProblems />} />

        <Route path={`${ERoute.Projects}/:projectScreenName/*`} element={<ProjectHub />} />
        <Route path={`${ERoute.Challenges}/:challengeScreenName/*`} element={<ChallengeHub />} />
        <Route
          path={`${ERoute.PracticeProblems}/:practiceProblemScreenName/*`}
          element={<PracticeProblemHub />}
        />

        {/* Create routes */}
        <Route path={ERoute.Prototypes} element={<Prototypes />} />
        <Route path={`${ERoute.Prototypes}${ERoute.NewPrototype}`} element={<NewPrototype />} />
        <Route
          path={`${ERoute.Prototypes}/:userScreenName/:prototypeScreenName/*`}
          element={<PrototypeHub />}
        />
        <Route path={ERoute.CodeFrames} element={<CodeFrames />} />
        <Route path={`${ERoute.CodeFrames}${ERoute.NewCodeFrame}`} element={<NewCodeFrame />} />
        <Route
          path={`${ERoute.CodeFrames}/:userScreenName/:codeFrameScreenName/*`}
          element={<CodeFrameHub />}
        />

        {/* CV Builder routes */}
        <Route path={ERoute.CVBuilder} element={<CVBuilder />} />
        <Route path={`${ERoute.CVBuilder}${ERoute.NewCV}`} element={<NewCV />} />
        <Route path={`${ERoute.CVBuilder}${ERoute.ImportingCV}`} element={<CVStreaming />} />
        <Route path={`${ERoute.CVBuilder}${ERoute.GeneratingCV}`} element={<CVStreaming />} />
        <Route path={`${ERoute.CVBuilder}/:cvId`} element={<CVEditor />} />

        {/* Roast My Project routes */}
        <Route path={ERoute.Roasts} element={<Roasts />} />
        <Route path={`${ERoute.Roasts}/:roastId`} element={<RoastDetail />} />

        {/* Demo route */}
        <Route path={ERoute.Demo} element={<Demo />} />
        <Route path={`${ERoute.Demo}/:template/*`} element={<Demo />} />

        <Route path={`${ERoute.Profile}/:profileScreenName`} element={<PreviewProfile />} />
        <Route path={ERoute.Plans} element={<Pricing />} />
        <Route path={ERoute.UnlockBig} element={<Pricing />} />
        <Route path="/pricing" element={<Navigate to={ERoute.Plans} replace />} />
        <Route path={ERoute.Profile} element={<EditProfile />} />
        <Route
          path={ERoute.Billing}
          element={
            <BillingProvider>
              <Billing />
            </BillingProvider>
          }
        />
        <Route path={ERoute.Settings} element={<Settings />} />
        <Route path={ERoute.DiscordCallback} element={<DiscordCallback />} />
        <Route
          path={ERoute.StripeCallback}
          element={
            <BillingProvider>
              <StripeCallback />
            </BillingProvider>
          }
        />
        <Route path="*" element={<Navigate to={ERoute.Dashboard} replace />} />
      </RouterRoutes>
    </AppLayout>
  );
};

const OnboardingOrAppRoutes = () => {
  const { onboardingStep, isFree, isLegacy } = useCurrentUser();

  // Legacy free users get full access (grandfathered)
  if (isLegacy) {
    return (
      <AchievementProvider>
        <AppRoutes />
      </AchievementProvider>
    );
  }

  if (onboardingStep === 'profile') {
    return <ProfileRoutes />;
  }

  if (onboardingStep === 'personalize') {
    return <PersonalizeRoutes />;
  }

  if (onboardingStep === 'paywall') {
    return <PaywallRoutes />;
  }

  // Free users who completed onboarding but have no active subscription/trial
  // Keep them in-app with access to pricing, demo, public solutions, and profiles
  if (isFree && !isLegacy) {
    return (
      <AppLayout isFreeUser hasDiscordRegistered={false}>
        <RouterRoutes>
          {/* Demo */}
          <Route path={ERoute.Demo} element={<Demo />} />
          <Route path={`${ERoute.Demo}/:template/*`} element={<Demo />} />

          {/* Pricing & re-subscribe */}
          <Route path={ERoute.Plans} element={<Pricing />} />
          <Route path={ERoute.UnlockBig} element={<Pricing />} />
          <Route path="/pricing" element={<Navigate to={ERoute.Plans} replace />} />
          <Route
            path={ERoute.StripeCallback}
            element={
              <BillingProvider>
                <StripeCallback />
              </BillingProvider>
            }
          />

          {/* Account management — accessible even when expired */}
          <Route path={`${ERoute.Profile}/:profileScreenName`} element={<PreviewProfile />} />
          <Route path={ERoute.Profile} element={<EditProfile />} />
          <Route
            path={ERoute.Billing}
            element={
              <BillingProvider>
                <Billing />
              </BillingProvider>
            }
          />
          <Route path={ERoute.Settings} element={<Settings />} />

          {/* Hub routes — Hub's internal routing handles solution vs playground/submit.
               Expired free users hitting the playground catch-all get redirected to demo. */}
          <Route path={`${ERoute.Projects}/:projectScreenName/*`} element={<ProjectHub />} />
          <Route
            path={`${ERoute.Challenges}/:challengeScreenName/*`}
            element={<ChallengeHub />}
          />
          <Route
            path={`${ERoute.PracticeProblems}/:practiceProblemScreenName/*`}
            element={<PracticeProblemHub />}
          />
          <Route
            path={`${ERoute.Prototypes}/:userScreenName/:prototypeScreenName/*`}
            element={<PrototypeHub />}
          />
          <Route
            path={`${ERoute.CodeFrames}/:userScreenName/:codeFrameScreenName/*`}
            element={<CodeFrameHub />}
          />

          {/* Everything else → in-app pricing */}
          <Route path="*" element={<Navigate to={ERoute.Plans} replace />} />
        </RouterRoutes>
      </AppLayout>
    );
  }

  return (
    <AchievementProvider>
      <AppRoutes />
    </AchievementProvider>
  );
};

export const Routes = () => {
  const { session, userId, loading } = useAuth();

  const [searchParams] = useSearchParams();

  useEffect(() => {
    const refCode = searchParams.get(REF_COOKIE_KEY) as string;

    if (refCode) {
      setCookie(REF_COOKIE_KEY, refCode);
    }

    // Capture plan preference from landing page CTAs (e.g. ?plan=monthly)
    const planSlug = searchParams.get('plan');
    if (planSlug) {
      savePlanPreference(planSlug);
    }

    // Capture fbclid from Meta ad clicks and generate fbp for browser identification
    captureFbclid();
    generateFbp();

    // Initialize Meta Pixel with consent check (iubenda)
    initPixelWithConsent();
  }, [searchParams]);

  // While auth is resolving, show a blank page to prevent layout flash
  // (sidebar/header rendering before we know the user's auth state)
  if (loading) {
    return null;
  }

  const renderProperRoutes = () => {
    if (!session) {
      return <UnauthenticatedRoutes />;
    }

    if (userId) {
      return <OnboardingOrAppRoutes />;
    }

    return <OnboardingRoutes />;
  };

  return (
    <CurrentUserProvider userId={userId} isAuthLoading={loading}>
      <RouterRoutes>
        {/* Public CV share link — renders standalone without AppLayout */}
        <Route path={`${ERoute.CVShare}/:cvId`} element={<CVPublicView />} />
        <Route path="*" element={renderProperRoutes()} />
      </RouterRoutes>
    </CurrentUserProvider>
  );
};
